/**
 * FileRow Component
 * Displays a file or folder in list/row format with selection and favourites support
 */

import React, { useState } from 'react';
import ObsoleteBadge from './ObsoleteBadge';

// File type to icon mapping
const getFileIcon = (extension, isFolder) => {
  if (isFolder) return { icon: 'fa-folder', className: 'folder' };

  const ext = (extension || '').toLowerCase();
  const iconMap = {
    pdf: { icon: 'fa-file-pdf', className: 'pdf' },
    doc: { icon: 'fa-file-word', className: 'doc' },
    docx: { icon: 'fa-file-word', className: 'docx' },
    xls: { icon: 'fa-file-excel', className: 'xls' },
    xlsx: { icon: 'fa-file-excel', className: 'xlsx' },
    ppt: { icon: 'fa-file-powerpoint', className: 'ppt' },
    pptx: { icon: 'fa-file-powerpoint', className: 'pptx' },
    txt: { icon: 'fa-file-alt', className: 'txt' },
    csv: { icon: 'fa-file-csv', className: 'csv' },
    jpg: { icon: 'fa-file-image', className: 'image' },
    jpeg: { icon: 'fa-file-image', className: 'image' },
    png: { icon: 'fa-file-image', className: 'image' },
    gif: { icon: 'fa-file-image', className: 'image' },
    svg: { icon: 'fa-file-image', className: 'image' },
    mp4: { icon: 'fa-file-video', className: 'video' },
    mov: { icon: 'fa-file-video', className: 'video' },
    mp3: { icon: 'fa-file-audio', className: 'audio' },
    wav: { icon: 'fa-file-audio', className: 'audio' },
    zip: { icon: 'fa-file-archive', className: 'archive' },
    rar: { icon: 'fa-file-archive', className: 'archive' },
  };

  return iconMap[ext] || { icon: 'fa-file', className: 'default' };
};

// Format file size
const formatFileSize = (bytes) => {
  if (!bytes) return '';
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  return `${(bytes / Math.pow(1024, i)).toFixed(1)} ${sizes[i]}`;
};

const FileRow = ({
  file,
  isFolder = false,
  onClick,
  onDownload,
  isSelected = false,
  onSelect,
  selectionMode = false,
  isFavourite = false,
  onToggleFavourite,
  onDownloadCurrent
}) => {
  const [downloading, setDownloading] = useState(false);
  const [togglingFavourite, setTogglingFavourite] = useState(false);

  const { icon, className } = getFileIcon(file.extension, isFolder);
  const previewUrl = file.friendly_url || file.s3_url;

  const handleClick = (e) => {
    e.preventDefault();
    if (selectionMode && !isFolder) {
      onSelect(file.id);
    } else if (isFolder) {
      onClick(file.id, file.name);
    }
  };

  const handleCheckboxChange = (e) => {
    e.stopPropagation();
    onSelect(file.id);
  };

  const handleDownload = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (isFolder || downloading) return;

    setDownloading(true);
    try {
      await onDownload(file.id, file.name, file.extension);
    } finally {
      setDownloading(false);
    }
  };

  const handlePreview = (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (previewUrl) {
      window.open(previewUrl, '_blank', 'noopener,noreferrer');
    }
  };

  const handleToggleFavourite = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (togglingFavourite || !onToggleFavourite) return;

    setTogglingFavourite(true);
    try {
      await onToggleFavourite(file.id, isFolder ? 'folder' : 'file');
    } finally {
      setTogglingFavourite(false);
    }
  };

  return (
    <div
      className={`rf-file-row ${isSelected ? 'selected' : ''} ${isFavourite ? 'favourited' : ''} ${file.obsolete ? 'obsolete' : ''}`}
      onClick={handleClick}
    >
      {/* Selection checkbox for files */}
      {!isFolder && (
        <div className="rf-file-row-checkbox">
          <input
            type="checkbox"
            checked={isSelected}
            onChange={handleCheckboxChange}
            onClick={(e) => e.stopPropagation()}
            aria-label={`Select ${file.name}`}
          />
        </div>
      )}

      <div className={`rf-file-row-icon ${className}`}>
        <i className={`fas ${icon}`}></i>
      </div>

      <div className="rf-file-row-info">
        <h4 className="rf-file-row-name" title={file.name}>
          {file.name}
        </h4>
        <div className="rf-file-row-meta">
          {!isFolder && file.extension && (
            <span>{file.extension.toUpperCase()}</span>
          )}
          {file.size && <span>{formatFileSize(file.size)}</span>}
          {isFolder && <span>Folder</span>}
        </div>
        {!isFolder && <ObsoleteBadge obsolete={file.obsolete} onDownloadCurrent={onDownloadCurrent} />}
      </div>

      <div className="rf-file-row-actions">
        {/* Favourite button */}
        {onToggleFavourite && (
          <button
            className={`rf-file-row-action favourite ${isFavourite ? 'active' : ''}`}
            onClick={handleToggleFavourite}
            disabled={togglingFavourite}
            title={isFavourite ? 'Remove from favourites' : 'Add to favourites'}
          >
            <i className={`${isFavourite ? 'fas' : 'far'} fa-star`}></i>
          </button>
        )}

        {isFolder ? (
          <button className="rf-file-row-action" onClick={handleClick} title="Open folder">
            <i className="fas fa-folder-open"></i>
          </button>
        ) : (
          <>
            {previewUrl && (
              <button className="rf-file-row-action" onClick={handlePreview} title="View file">
                <i className="fas fa-eye"></i>
              </button>
            )}
            <button
              className="rf-file-row-action download"
              onClick={handleDownload}
              disabled={downloading}
              title="Download file"
            >
              {downloading ? (
                <i className="fas fa-spinner fa-spin"></i>
              ) : (
                <i className="fas fa-download"></i>
              )}
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default FileRow;
